import { defineStore } from "pinia";
import { computed, ref } from "vue";
import instanceJWT from "../../api/apiJwt";
import {
  parseMakeOrderResult,
  resolveOrderPlacement,
  type MakeOrderResult,
} from "../../common/helpers/eplex/makeOrderResult";
import { describeOrderRejections } from "../../common/helpers/eplex/journalOrderStatus";
import type { QuickCartItem } from "../../common/helpers/eplex/quickCartGrouping";
import { mapQuickCartItem } from "../../common/helpers/eplex/quickCartItemMapper";
import { orderRejectionReason } from "../../common/helpers/eplex/quickCartPlacement";
import { useNotifications } from "../useNotifications";
import { useQuickCartPlacementStore } from "./eplexQuickCartPlacement";
import i18n from "../../../i18n";
import {
  hasTrialRestrictedItem,
  isTrialRestrictedError,
  isTrialRestrictedItemError,
} from "../../common/helpers/eplex/trialRestriction";

// Окно отмены: позиция остаётся в списке (приглушённая) с инлайн-отменой,
// заказ уходит на бэк только по истечении окна.
const UNDO_DELAY_MS = 5000;

/** Итог оформления заказа: что не прошло и почему (если отказ был общим). */
interface OrderOutcome {
  ok: boolean;
  failed: string[];
  /** Текст отказа всей операции от бэка; null — отказа по операции не было. */
  error: string | null;
  /**
   * Причина отказа по позициям («Закончились токены» для −3): бэк её либо
   * присылает текстом, либо кодирует статусом. Без неё об отказе оставалось
   * сказать только «ошибка при оформлении» (CAB-602 п.2).
   */
  reason: string | null;
  /** Отказ по демо-доступу: объяснение вместо ошибки (CAB-670). */
  trialRestricted: boolean;
}

/** Объединяет два списка позиций по thash (incoming перекрывает base). */
function mergeByThash(base: QuickCartItem[], incoming: QuickCartItem[]): QuickCartItem[] {
  const byThash = new Map(base.map((i) => [i.thash, i]));
  for (const item of incoming) byThash.set(item.thash, item);
  return [...byThash.values()];
}

const translate = (key: string, params?: Record<string, unknown>) => i18n.global.t(key, params ?? {});

function emptyOutcome(): OrderOutcome {
  return { ok: true, failed: [], error: null, reason: null, trialRestricted: false };
}

export const useQuickCartStore = defineStore("quickCart", () => {
  const items = ref<QuickCartItem[]>([]);
  // Оформленные позиции подбора: ушли в заказ, но остаются привязанными к потребности ГП
  // (status ≠ 0 + есть sreqid). Продолжают засчитываться в обеспеченность — заказанный товар
  // не должен «пропадать» из плана и делать потребность необеспеченной (CAB-560).
  const orderedItems = ref<QuickCartItem[]>([]);
  const loading = ref(false);

  // Быстрое добавление нескольких позиций подряд запускает несколько параллельных
  // load(): ответы могут прийти не по порядку, и более ранний (с меньшим числом
  // позиций) перезатирает более свежий — бейдж «застревает» (CAB-590). Применяем
  // только ответ последнего запущенного запроса.
  let loadSeq = 0;

  // thash'и позиций в окне отмены (остаются видимыми в списке, но приглушены)
  const pendingThashes = ref<string[]>([]);
  let pendingTimer: ReturnType<typeof setTimeout> | null = null;

  // Причины отказа по позициям, которые бэк отклонил в момент оформления и
  // которые остались в корзине (CAB-782). Отметка держится на строке, пока
  // позицию не отправят снова или не удалят: тост закрыли — причина осталась.
  const rejections = ref<Record<string, string>>({});

  const count = computed(() => items.value.length);
  const hasPending = computed(() => pendingThashes.value.length > 0);

  function isPending(thash: string): boolean {
    return pendingThashes.value.includes(thash);
  }

  function rejectionOf(thash: string): string | null {
    return rejections.value[thash] ?? null;
  }

  function clearRejections(thashes: readonly string[]) {
    if (!thashes.some((thash) => thash in rejections.value)) return;
    const next = { ...rejections.value };
    for (const thash of thashes) delete next[thash];
    rejections.value = next;
  }

  async function load() {
    const seq = ++loadSeq;
    loading.value = true;
    try {
      const params = new URLSearchParams({ type: "1", limit: "max", offset: "0" });
      const res = await instanceJWT.get(`eplex/orders/journal?${params.toString()}`);
      // ответ устарел — его обогнал более поздний load(), не перезатираем актуальные данные
      if (seq !== loadSeq) return;
      const raw: Record<string, unknown>[] = res.data?.data?.data ?? [];
      // status 0 = в корзине (ещё не заказано)
      items.value = raw.filter((r) => Number(r.status) === 0).map(mapQuickCartItem);
      syncOrderedFromJournal(raw);
      // отметки отказа — только у позиций, которые ещё в корзине
      const present = new Set(items.value.map((i) => i.thash));
      clearRejections(Object.keys(rejections.value).filter((thash) => !present.has(thash)));
      // судьба ранее отправленных заказов сверяется с журналом тем же обновлением
      useQuickCartPlacementStore().refresh();
    } catch (error) {
      console.error("Quick cart load error: ", error);
      if (seq === loadSeq) items.value = [];
    } finally {
      if (seq === loadSeq) loading.value = false;
    }
  }

  /** Пересобирает список оформленных позиций подбора из журнала, сохраняя оптимистично
   *  оформленные в этой сессии (на случай, если бэк не вернул им sreqid). */
  function syncOrderedFromJournal(raw: Record<string, unknown>[]) {
    const backendOrdered = raw
      .filter((r) => Number(r.status) !== 0 && typeof r.sreqid === "string" && r.sreqid)
      .map((r) => ({ ...mapQuickCartItem(r), ordered: true }));
    const activeThashes = new Set(items.value.map((i) => i.thash));
    // выкидываем те, что вернулись в корзину (снова status 0)
    const retained = orderedItems.value.filter((i) => !activeThashes.has(i.thash));
    orderedItems.value = mergeByThash(retained, backendOrdered);
  }

  /** Запускаем окно отмены: позиции остаются в списке (приглушёнными), по таймеру — реальный заказ. */
  function scheduleOrder(toOrder: QuickCartItem[]) {
    if (!toOrder.length) return;
    flushPending();
    pendingThashes.value = toOrder.map((i) => i.thash);
    // новая попытка — прежний отказ по этим позициям больше не актуален
    clearRejections(pendingThashes.value);
    pendingTimer = setTimeout(() => {
      void finalizeOrder().then(reportScheduledOrderOutcome);
    }, UNDO_DELAY_MS);
  }

  /** Отмена: снимаем «ожидание», позиции снова обычные. */
  function undoOrder() {
    if (pendingTimer) {
      clearTimeout(pendingTimer);
      pendingTimer = null;
    }
    pendingThashes.value = [];
  }

  /**
   * Реальная отправка заказа на бэк. Успешные позиции убираем из списка,
   * ошибочные оставляем. `error` — причина отказа всей операции от бэка
   * (например «Недостаточно средств»), если он был.
   */
  async function finalizeOrder(): Promise<OrderOutcome> {
    if (pendingTimer) {
      clearTimeout(pendingTimer);
      pendingTimer = null;
    }
    const thashes = pendingThashes.value;
    pendingThashes.value = [];
    if (!thashes.length) return emptyOutcome();

    const ordered = thashes.flatMap((thash) => {
      const item = items.value.find((i) => i.thash === thash);
      return item ? [item] : [];
    });
    const orderItems = Object.fromEntries(ordered.map((item) => [item.thash, { qty: item.qty }]));

    try {
      const res = await instanceJWT.post("eplex/temp_cart/make_order", { items: orderItems });
      const result = parseMakeOrderResult(res.data);

      // Отказ всей операции (нет токенов, лимит, апстрим недоступен): ни одна
      // позиция не заказана — оставляем корзину как есть, иначе позиции «исчезают»
      // при том, что заказа нет (CAB-602 п.2).
      if (result.error !== null || (!result.ok && result.rejected.length === 0)) {
        return rejectWholeOrder(thashes, result.error);
      }

      return applyOrderResult(ordered, result);
    } catch (error) {
      if (isTrialRestrictedError(error)) {
        return { ok: false, failed: thashes, error: null, reason: null, trialRestricted: true };
      }

      console.error("Quick cart order error: ", error);
      // при ошибке ничего не убираем — позиции остаются в корзине
      return { ok: false, failed: thashes, error: null, reason: null, trialRestricted: false };
    }
  }

  /** Бэк отказал по всей операции: позиции остаются в корзине с общей причиной на каждой. */
  function rejectWholeOrder(thashes: string[], error: string | null): OrderOutcome {
    const trialRestricted = isTrialRestrictedItemError(error);
    // отказ по демо-доступу объясняет модалка подбора — отметка на строках не нужна
    if (!trialRestricted) markRejected(thashes.map((thash) => [thash, error ?? translate("quickCart.orderFailed")]));
    return { ok: false, failed: thashes, error, reason: null, trialRestricted };
  }

  /**
   * Сопоставляем ответ с отправленными позициями по ключу запроса — тем же,
   * которым бэк отвечает: вычитание списков молча теряло отказы (CAB-602 п.2).
   *
   * Пока отказов нет, доверяем HTTP 200 и убираем из корзины всё отправленное
   * (так фронт вёл себя всегда). Но если по какой-то позиции пришёл отказ,
   * «оформленными» считаем только те, приём которых бэк подтвердил их же
   * ключом: иначе неопознанный отказ вычищал корзину при отсутствии заказа.
   */
  function applyOrderResult(ordered: QuickCartItem[], result: MakeOrderResult): OrderOutcome {
    const placement = resolveOrderPlacement(ordered, result);
    const placedThashes = new Set(placement.placed.map((item) => item.thash));
    const successful = result.rejected.length === 0 ? ordered : ordered.filter((item) => placedThashes.has(item.thash));
    const successfulThashes = new Set(successful.map((item) => item.thash));
    const failed = ordered.map((item) => item.thash).filter((thash) => !successfulThashes.has(thash));
    const trialRestricted = hasTrialRestrictedItem(result.rejected);

    // причина отказа остаётся на строке, пока позицию не отправят снова (CAB-782)
    if (!trialRestricted) {
      markRejected(
        placement.rejected.map(({ item, status, error }) => [
          item.thash,
          orderRejectionReason(status, error, translate),
        ]),
      );
    }

    // успешно оформленные позиции подбора (с привязкой к потребности) переносим в
    // «оформленные» — они остаются в плане и продолжают засчитываться в обеспеченность
    const movedOrdered = successful.filter((i) => i.sreqid).map((i) => ({ ...i, ordered: true }));
    orderedItems.value = mergeByThash(orderedItems.value, movedOrdered);
    // успешные убираем из корзины (ошибочные оставляем)
    items.value = items.value.filter((i) => !successfulThashes.has(i.thash));
    // приём заявки — ещё не заказ: итог придёт статусом строки, за ним следим (CAB-782)
    useQuickCartPlacementStore().track(successful);

    // отказ по демо-доступу приезжает пер-позиционно: заказ отклонён целиком,
    // и причину надо показать объяснением, а не «ошибкой» (CAB-670)
    return {
      ok: failed.length === 0,
      failed,
      error: null,
      reason: describeOrderRejections(result.rejected, (key) => i18n.global.t(key)) || null,
      trialRestricted,
    };
  }

  function markRejected(entries: Array<[thash: string, reason: string]>) {
    if (!entries.length) return;
    rejections.value = { ...rejections.value, ...Object.fromEntries(entries) };
  }

  /**
   * Сообщить об отказе отложенного заказа. Окно отмены отправляет заказ по
   * таймеру — результата никто не ждёт, и отказ (в т.ч. «закончились токены»)
   * оставался вообще без объяснения: позиции просто оставались в корзине
   * (CAB-602 п.2). Демо-доступ здесь не трогаем — его объясняет подбор модалкой.
   */
  function reportScheduledOrderOutcome(outcome: OrderOutcome) {
    if (outcome.ok || outcome.trialRestricted) return;
    // тост об отказе не исчезает сам: причину нужно успеть прочитать (CAB-782)
    const reason = outcome.error ?? outcome.reason ?? translate("quickCart.orderFailed");
    useNotifications().notifySticky(translate("quickCart.orderRejected", { reason }), "error");
  }

  /** Немедленно дослать ожидающий заказ (без ожидания окна) — напр. при закрытии. */
  function flushPending() {
    if (pendingTimer) {
      void finalizeOrder().then(reportScheduledOrderOutcome);
    }
  }

  /** Немедленно оформить заказ по указанным позициям (без окна отмены). Успешные
   *  убираем из корзины, ошибочные оставляем. Используется в «Ручном режиме» подбора. */
  async function orderNow(toOrder: QuickCartItem[]): Promise<OrderOutcome> {
    if (!toOrder.length) return emptyOutcome();
    pendingThashes.value = toOrder.map((i) => i.thash);
    clearRejections(pendingThashes.value);
    return finalizeOrder();
  }

  /** Убрать позицию из корзины (оптимистично + DELETE на бэк, откат при ошибке). */
  async function removeItem(thash: string) {
    const idx = items.value.findIndex((i) => i.thash === thash);
    if (idx === -1) return;
    const removed = items.value[idx];
    items.value.splice(idx, 1);
    try {
      await instanceJWT.delete("eplex/temp_cart", { data: { items: [thash] } });
      clearRejections([thash]);
    } catch (error) {
      console.error("Quick cart remove error: ", error);
      items.value.splice(idx, 0, removed);
      // позиция вернулась на место — без объяснения это выглядит как «кнопка не работает»
      useNotifications().notify(translate("quickCart.removeFailed"), "error");
    }
  }

  /** Изменить количество позиции (оптимистично + POST на бэк, откат при ошибке). */
  async function updateQty(thash: string, qty: number) {
    if (qty < 1) return;
    const item = items.value.find((i) => i.thash === thash);
    if (!item || item.qty === qty) return;
    const prev = item.qty;
    item.qty = qty;
    try {
      await instanceJWT.post(`eplex/temp_cart/${thash}`, { item: { qty } });
    } catch (error) {
      console.error("Quick cart qty update error: ", error);
      item.qty = prev;
    }
  }

  return {
    items,
    orderedItems,
    loading,
    pendingThashes,
    rejections,
    count,
    hasPending,
    isPending,
    rejectionOf,
    load,
    scheduleOrder,
    undoOrder,
    finalizeOrder,
    flushPending,
    orderNow,
    removeItem,
    updateQty,
  };
});
